import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import serviceApi from '../services/serviceApi';
import { formatDate } from '../utils/dateFormatter';
import '../styles/ServiceDashboard.css';

const ServiceDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeNavItem, setActiveNavItem] = useState('services');

  const [metrics, setMetrics] = useState({
    activeClients: 0,
    activeSubscriptions: 0,
    upcomingRequests: 0,
    lowBalanceAlerts: 0,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const clientsRes = await serviceApi.getClients();
      const clients = clientsRes.data?.clients || [];

      const activeClients = clients.filter(c => c.status === 'active').length;

      // Fetch subscriptions and requests for each active client
      let totalActiveSubs = 0;
      let totalLowBalance = 0;
      let totalUpcoming = 0;

      const now = new Date();
      const weekFromNow = new Date(now);
      weekFromNow.setDate(weekFromNow.getDate() + 7);

      await Promise.all(clients.filter(c => c.status === 'active').map(async (client) => {
        try {
          const [subsRes, reqsRes] = await Promise.all([
            serviceApi.getClientSubscriptions(client._id),
            serviceApi.getClientRequests(client._id),
          ]);

          const subs = subsRes.data?.subscriptions || [];
          const reqs = reqsRes.data?.requests || [];

          const activeSubs = subs.filter(s => s.status === 'active');
          totalActiveSubs += activeSubs.length;

          totalLowBalance += activeSubs.filter(s => (s.effectiveBalance ?? 0) <= 2).length;

          totalUpcoming += reqs.filter(r => {
            if (r.status === 'completed' || r.status === 'cancelled') return false;
            const reqDate = new Date(r.requestedDate);
            return reqDate >= now && reqDate <= weekFromNow;
          }).length;
        } catch {
          // Skip individual client fetch errors
        }
      }));

      setMetrics({
        activeClients,
        activeSubscriptions: totalActiveSubs,
        upcomingRequests: totalUpcoming,
        lowBalanceAlerts: totalLowBalance,
      });
    } catch (err) {
      console.error('خطأ في تحميل بيانات خدمات النظافة:', err);
      setError('تعذر تحميل البيانات — يرجى المحاولة مرة أخرى');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem}>
        <div className="svc-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  if (error) {
    return (
      <DesktopLayout activeNavItem={activeNavItem} setActiveNavItem={setActiveNavItem}>
        <div className="svc-error">
          <div className="svc-error__icon"><i className="bi bi-exclamation-triangle" /></div>
          <p className="svc-error__text">{error}</p>
          <button className="svc-error__retry" onClick={fetchData}>
            <i className="bi bi-arrow-clockwise" />
            إعادة المحاولة
          </button>
        </div>
      </DesktopLayout>
    );
  }

  const cards = [
    {
      key: 'clients',
      value: metrics.activeClients,
      label: 'عملاء نشطون',
      sub: 'إجمالي العملاء الفعالين',
      icon: 'bi-people',
      accent: '#471396',
      onClick: () => navigate('/services/clients'),
    },
    {
      key: 'subscriptions',
      value: metrics.activeSubscriptions,
      label: 'اشتراكات فعالة',
      sub: 'اشتراكات تنظيف نشطة',
      icon: 'bi-card-checklist',
      accent: '#28a745',
    },
    {
      key: 'upcoming',
      value: metrics.upcomingRequests,
      label: 'طلبات قادمة',
      sub: 'خلال الـ 7 أيام القادمة',
      icon: 'bi-calendar-week',
      accent: '#17a2b8',
      onClick: () => navigate('/services/requests?status=upcoming'),
    },
    {
      key: 'lowBalance',
      value: metrics.lowBalanceAlerts,
      label: 'تنبيهات الرصيد',
      sub: 'اشتراكات برصيد منخفض',
      icon: 'bi-exclamation-triangle',
      accent: '#dc3545',
      pulse: metrics.lowBalanceAlerts > 0,
    },
  ];

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
    >
      <div className="svc-dashboard">
        {/* Header */}
        <div className="svc-header">
          <h3 className="svc-title">خدمات النظافة</h3>
          <span className="svc-date">
            {formatDate(new Date(), { weekday: 'long' })}
          </span>
        </div>

        {/* Metrics */}
        <div className="svc-section">
          <h4 className="svc-section-title">نظرة عامة</h4>
          <div className="svc-cards-grid">
            {cards.map((card, i) => (
              <div
                key={card.key}
                className={`svc-card${card.pulse ? ' svc-card--alert' : ''}`}
                style={{
                  '--card-accent': card.accent,
                  animationDelay: `${i * 80}ms`,
                }}
                onClick={card.onClick}
                role={card.onClick ? 'button' : undefined}
                tabIndex={card.onClick ? 0 : undefined}
              >
                <div className="svc-card__icon-wrap">
                  <i className={`bi ${card.icon} svc-card__icon`} />
                </div>
                <div className="svc-card__body">
                  <span className="svc-card__value">{card.value}</span>
                  <span className="svc-card__label">{card.label}</span>
                  <span className="svc-card__sub">{card.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="svc-section">
          <h4 className="svc-section-title">إجراءات سريعة</h4>
          <div className="svc-actions">
            <button
              className="svc-action svc-action--primary"
              onClick={() => navigate('/services/clients')}
            >
              <i className="bi bi-people" />
              عملاء الخدمة
            </button>
            <button
              className="svc-action svc-action--outline"
              onClick={() => navigate('/services/requests/new')}
            >
              <i className="bi bi-plus-circle" />
              طلب تنظيف جديد
            </button>
            <button
              className="svc-action svc-action--outline"
              onClick={() => navigate('/services/clients/new')}
            >
              <i className="bi bi-person-plus" />
              إضافة عميل
            </button>
            <button
              className="svc-action svc-action--outline"
              onClick={() => navigate('/services/invoices')}
            >
              <i className="bi bi-receipt" />
              الفواتير
            </button>
          </div>
        </div>
      </div>
    </DesktopLayout>
  );
};

export default ServiceDashboard;
